import { useApplicationDefaultLanguage, useSelectedBackofficeLanguages } from "../../../utils/languageHooks";
import { LanguageAvailableInConfig, languagesAvailableInConfig } from "../TranslatableEditor/translations_definitions";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Button, List, ListItem, TextField } from "@mui/material";
import { LanguageSelect } from "../TranslatableEditor/LanguageSelect";
import { TranslationsStorage } from "../TranslatableEditor/TranslatableEditor.d";
import axios from "axios";
import { useWatch } from "react-hook-form";
import _ from "lodash";
import { addMissingLanguages } from "../TranslatableEditor/TranslatableEditor";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper";

export function useLanguages() {
  const defaultLanguage = useApplicationDefaultLanguage();
  const configLanguages = useSelectedBackofficeLanguages(defaultLanguage);
  const activeLanguages = languagesAvailableInConfig
    .filter(({ value }) => configLanguages.includes(value) || value == defaultLanguage)
    .map(({ value }) => value);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageAvailableInConfig | undefined>(
    languagesAvailableInConfig.find(({ value }) => value === defaultLanguage)
  );
  if (selectedLanguage === undefined) {
    throw new TypeError(`language: "${defaultLanguage}" should exist in languagesAvailableInConfig`);
  }

  const changeLanguage = (e: ChangeEvent<{ name?: string; value: unknown }>) => {
    const newLang = languagesAvailableInConfig.find(({ value }) => value === e.target.value);
    if (newLang === undefined) {
      throw new TypeError(
        `language: "${e.target.value}" should exist in languagesAvailableInConfig: ${languagesAvailableInConfig}`
      );
    }
    setSelectedLanguage(newLang);
  };

  return {
    setSelectedLanguage,
    activeLanguages,
    selectedLanguage,
    changeLanguage,
  };
}

function CustomizableText({
  text,
  value,
  onChange,
  selectedLanguage,
}: {
  text: string;
  value: string;
  onChange: (value: string) => void;
  selectedLanguage: LanguageAvailableInConfig;
}) {
  return (
    <div>
      <p>{text}</p>
      <TextField
        multiline
        onChange={(e) => onChange(e.currentTarget.value)}
        placeholder={`no text for ${selectedLanguage.value} yet`}
        style={{ width: "20rem" }}
        value={value}
      />
    </div>
  );
}

function useSaveTranslations(endpoint: string, vestigingId: number) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const saveTranslations = useCallback(
    async (translations: TranslationsStorage) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post(endpoint, { id: vestigingId, translations });
      } catch (err) {
        setError(err as Error);
      }
      setLoading(false);
    },
    [endpoint, vestigingId]
  );

  return { loading, error, saveTranslations };
}

export type ApiTranslation = { translation_key: string; text: string; language_code: string };

export function useCustomTranslationsStorage({
  modelId,
  source_translations,
  translatableTexts,
  model,
}: {
  modelId: number;
  source_translations: ApiTranslation[];
  translatableTexts: string[];
  model: string;
}) {
  const [translations, setTranslations] = useState<TranslationsStorage | null>(null);
  const [dirtyTranslations, setDirtyTranslations] = useState<ReadonlyArray<string>>([]);

  const { loading, error, saveTranslations } = useSaveTranslations(`/core/custom_translations/${model}/save`, modelId);

  useEffect(() => {
    const t =
      _.isArray(source_translations) && source_translations.length === 0
        ? {}
        : source_translations
          ? source_translations.reduce((acc: TranslationsStorage, { language_code, text, translation_key }) => {
              if (!acc.hasOwnProperty(language_code)) {
                acc[language_code] = {};
              }
              acc[language_code][translation_key] = text;
              return acc;
            }, {})
          : {};
    addMissingLanguages(t, translatableTexts);
    setTranslations(t);
  }, [source_translations]);

  return {
    translations,
    dirtyTranslations,
    setDirtyTranslations,
    loading,
    error,
    saveTranslations,
    setTranslations,
  };
}

export function CustomizableTexts({
  translatableTexts,
  vestigingId,
}: {
  translatableTexts: string[];
  vestigingId: number;
}) {
  const { selectedLanguage, activeLanguages, changeLanguage } = useLanguages();

  const source_translations: ApiTranslation[] = useWatch({ name: "customizable_texts" });
  const { translations, dirtyTranslations, saveTranslations, loading, error, setDirtyTranslations, setTranslations } =
    useCustomTranslationsStorage({
      model: "vestiging",
      modelId: vestigingId,
      translatableTexts,
      source_translations,
    });

  return translations ? (
    <>
      <Button
        color={"secondary"}
        disabled={dirtyTranslations.length === 0 || loading}
        onClick={() => {
          saveTranslations(translations);
        }}
        variant={"contained"}
      >
        {loading ? (
          "Saving"
        ) : (
          <TranslationWrapper
            tDefault={"Save changes to custom texts"}
            tKey={"button.customizable-translations.save"}
          />
        )}
      </Button>
      <span style={{ marginLeft: "8px" }}>
        <LanguageSelect
          allowedLanguages={activeLanguages}
          language={selectedLanguage.value}
          onChange={(lang) => {
            changeLanguage(lang);
          }}
        />
      </span>
      <List>
        {translatableTexts.map((text) => {
          return (
            <ListItem key={text}>
              <CustomizableText
                onChange={(value) => {
                  // I love lodash!
                  _.set(translations, `[${selectedLanguage.value}][${text}]`, value);
                  setTranslations(_.cloneDeep(translations));
                  setDirtyTranslations(_.concat(dirtyTranslations, text));
                }}
                selectedLanguage={selectedLanguage}
                text={text}
                value={translations[selectedLanguage.value][text]}
              />
            </ListItem>
          );
        })}
      </List>
    </>
  ) : (
    <>loading</>
  );
}
